import type {
  DeviceListResponseV5,
  DeviceResponseV5,
  WorkspaceResponseV2,
} from '@emma/api_client/types';
import { Box, Button, Card, Flex, Icon, Input, Text, Tooltip } from '@lego/klik-ui';
import { CircleQuestionSolid, StatusTriangleExclamationSolid } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/common';
import {
  colorPriority,
  getAggregateColor,
  getConnectivityStatus,
  getContainerStatus,
  getDeploymentStatus,
  getOsUpdateAvailable,
  getOsVersionStatus,
  SubTrafficLight,
  type TrafficLightColor,
  TrafficLightDot,
} from '@/components/device/TrafficLight';
import { useApiFetch } from '@/hooks/api';
import { useFleets } from '@/hooks/fleet/fleets_resource';
import { useCanOperate } from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';
import { paths } from '@/utility/global_paths';
import { DeviceQuickActions } from './DeviceQuickActions';

type Props = {
  workspace: WorkspaceResponseV2;
};

const REFRESH_INTERVAL_MS = 30_000;
const WORKSPACE_DEVICES_PAGE_SIZE = 1000;

const FIREWALL_PREFIX_TO_SITE: Readonly<Record<string, { code: string; label: string }>> = {
  mx: { code: 'LOM', label: 'LOM - Mexico' },
  us: { code: 'LVA', label: 'LVA - USA' },
  dk: { code: 'KOM', label: 'KOM - Denmark' },
  cz: { code: 'KLA', label: 'KLA - Czech Republic' },
  hu: { code: 'NYI', label: 'NYI - Hungary' },
  vn: { code: 'LMV', label: 'LMV - Vietnam' },
  cn: { code: 'JIA', label: 'JIA - China' },
};

const UNKNOWN_SITE = { code: 'Unknown', label: 'Unknown Site' };

export const getSiteFromFirewallName = (
  firewallName: string | null | undefined
): { code: string; label: string } => {
  if (!firewallName) return UNKNOWN_SITE;
  const prefix = firewallName.slice(0, 2).toLowerCase();
  return FIREWALL_PREFIX_TO_SITE[prefix] ?? UNKNOWN_SITE;
};

type DeviceMetadata = Pick<
  DeviceResponseV5,
  'name' | 'workcenterId' | 'firewallName' | 'netboxLocation'
>;

export const getDeviceSite = (device: Pick<DeviceMetadata, 'firewallName' | 'netboxLocation'>) => {
  const site = device.netboxLocation?.site;
  return site
    ? { code: `netbox-${site.slug || site.name}`, label: site.name }
    : getSiteFromFirewallName(device.firewallName);
};

export const matchesDeviceFilter = (device: DeviceMetadata, filter: string): boolean => {
  const query = filter.trim().toLowerCase();
  return [
    device.name,
    device.workcenterId,
    device.netboxLocation?.site.name,
    device.netboxLocation?.location?.name,
    device.netboxLocation?.rack?.name,
    device.netboxLocation?.firewallAssetTag,
  ].some((value) => value?.toLowerCase().includes(query));
};

const useWorkspaceDevices = (workspaceId: number) => {
  return useApiFetch(
    `workspace-status-devices-${workspaceId}`,
    async (callApi) => {
      const devices: DeviceResponseV5[] = [];
      let offset = 0;
      let itemCount = 0;
      let lastResponse:
        | {
            data: DeviceListResponseV5;
            status: number;
            statusText: string;
            headers: Record<string, string>;
          }
        | undefined;

      do {
        const params = {
          workspaceIdIn: [workspaceId],
          stateIn: ['PROVISIONED' as const],
          limit: WORKSPACE_DEVICES_PAGE_SIZE,
          offset,
        };
        const response = await callApi((api) => api.devices.getV5Devices(params));
        const pageDevices = response.data?.devices ?? [];

        lastResponse = response;
        devices.push(...pageDevices);
        itemCount = response.data?.itemCount ?? devices.length;
        offset += pageDevices.length;

        if (pageDevices.length === 0) {
          break;
        }
      } while (offset < itemCount);

      return {
        ...lastResponse,
        data: {
          ...lastResponse?.data,
          devices,
          itemCount: Math.max(itemCount, devices.length),
        },
      };
    },
    { refreshInterval: REFRESH_INTERVAL_MS, keepPreviousData: true }
  );
};

type DeviceStatusCardProps = {
  device: DeviceResponseV5;
  fleetActiveDeploymentIds: Map<number, number | null>;
  canOperate: boolean;
  fleetNames: Map<number, string>;
};

const DeviceLocationLabels = ({
  device,
}: {
  device: Pick<DeviceMetadata, 'workcenterId' | 'netboxLocation'>;
}) => (
  <>
    <Text color="slate.600" fontSize="xs" overflowWrap="anywhere">
      Workcenter ID: {device.workcenterId ?? 'Unassigned'}
    </Text>
    <Text color="slate.600" fontSize="xs" overflowWrap="anywhere">
      Location: {device.netboxLocation?.location?.name ?? 'Unknown'}
    </Text>
  </>
);

const DeviceStatusCard = ({
  device,
  fleetActiveDeploymentIds,
  canOperate,
  fleetNames,
}: DeviceStatusCardProps) => {
  const navigate = useNavigate();

  const containerStatus = getContainerStatus(device.deployments);
  const connectivityStatus = getConnectivityStatus(device.isConnected);
  const deploymentStatus = getDeploymentStatus(device.deployments, fleetActiveDeploymentIds);
  const osStatus = getOsVersionStatus(device.os ?? undefined);
  const osUpdate = getOsUpdateAvailable(device.os ?? undefined, device.deviceType);

  const aggregateColor = getAggregateColor([
    containerStatus.color,
    connectivityStatus.color,
    deploymentStatus.color,
    osStatus.color,
  ]);

  const activeDeployments = device.deployments?.filter((d) => d.isActive) ?? [];
  const latestReportedAt = activeDeployments
    .map((d) => d.applicationStatusReportedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  const containerDescription =
    containerStatus.total > 0
      ? `${containerStatus.running}/${containerStatus.total} containers running`
      : 'No containers';

  const deploymentDescription =
    deploymentStatus.color === 'green'
      ? 'Expected deployment'
      : deploymentStatus.color === 'yellow'
        ? 'Deployment mismatch'
        : deploymentStatus.color === 'red'
          ? 'Deployment failed'
          : 'No deployment data';

  return (
    <Card
      as="button"
      boxShadow="md"
      cursor="pointer"
      display="flex"
      flexDirection="column"
      gap={3}
      onClick={() => navigate(paths.deviceById(device.id))}
      px={6}
      py={4}
      rounded="md"
      textAlign="left"
      width="300px"
    >
      <Flex alignItems="center" gap={3}>
        <TrafficLightDot color={aggregateColor} label="Overall device status" size="24px" />
        <Flex flexDirection="column" flexGrow={1} overflow="hidden">
          <Text fontWeight="bold" noOfLines={1}>
            {device.name}
          </Text>
          <Text color="slate.400" fontSize="xs">
            ID: {device.id}
          </Text>
          <DeviceLocationLabels device={device} />
        </Flex>
        {canOperate && <DeviceQuickActions device={device} fleetNames={fleetNames} />}
      </Flex>

      <Flex flexDirection="column" gap={2} pl={1}>
        <SubTrafficLight
          color={containerStatus.color}
          description={containerDescription}
          label={containerStatus.label}
        />
        <SubTrafficLight
          color={connectivityStatus.color}
          description={device.isConnected ? 'Connected' : 'Disconnected'}
          label={connectivityStatus.label}
        />
        <SubTrafficLight
          color={deploymentStatus.color}
          description={deploymentDescription}
          label={deploymentStatus.label}
        />
        <Flex alignItems="center" gap={1}>
          <SubTrafficLight
            color={osStatus.color}
            description={`OS ${device.os?.version ?? '?'}`}
            label={osStatus.label}
          />
          {osUpdate.available && (
            <Tooltip label={`OS update available: ${osUpdate.latestVersion}`}>
              <Box as="span" color="yellow.500" display="inline-flex" flexShrink={0}>
                <Icon as={StatusTriangleExclamationSolid} boxSize="14px" />
              </Box>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {latestReportedAt && (
        <Tooltip label={new Date(latestReportedAt).toLocaleString(dateTimeFormat.local)}>
          <Text color="slate.400" fontSize="xs">
            Last reported: {formatRelativeTime(latestReportedAt)}
          </Text>
        </Tooltip>
      )}
    </Card>
  );
};

const formatRelativeTime = (timestamp: string): string => {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);

  // Handle clock skew - clamp negative values to 0
  if (diffSeconds < 0) return '0s ago';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const WorkspaceStatusSubPage = ({ workspace }: Props) => {
  const [nameFilter, setNameFilter] = useState('');
  const [showLegend, setShowLegend] = useState(false);
  const { data, error, isLoading } = useWorkspaceDevices(workspace.id);
  const {
    data: fleetsData,
    error: fleetsError,
    isLoading: isFleetsLoading,
  } = useFleets(workspace.id);
  const canOperate = useCanOperate(workspace);

  if (isLoading || isFleetsLoading || error || fleetsError || !data || !fleetsData) {
    return <Loader />;
  }

  const devices: DeviceResponseV5[] = data.data?.devices ?? [];
  const fleets = fleetsData?.fleets ?? [];

  // Build map of fleetId -> activeDeploymentId
  const fleetActiveDeploymentIds = new Map<number, number | null>();
  const fleetNames = new Map<number, string>();
  for (const fleet of fleets) {
    fleetActiveDeploymentIds.set(fleet.id, fleet.activeDeploymentId ?? null);
    fleetNames.set(fleet.id, fleet.name);
  }

  const filteredDevices = devices.filter((device) => matchesDeviceFilter(device, nameFilter));

  // Group devices by site and sort by color priority within each group
  const getDeviceAggregateColor = (device: DeviceResponseV5): TrafficLightColor => {
    const containerStatus = getContainerStatus(device.deployments);
    const connectivityStatus = getConnectivityStatus(device.isConnected);
    const deploymentStatus = getDeploymentStatus(device.deployments, fleetActiveDeploymentIds);
    const osStatus = getOsVersionStatus(device.os ?? undefined);
    return getAggregateColor([
      containerStatus.color,
      connectivityStatus.color,
      deploymentStatus.color,
      osStatus.color,
    ]);
  };

  const sortByColorThenName = (a: DeviceResponseV5, b: DeviceResponseV5): number => {
    const colorDiff =
      colorPriority[getDeviceAggregateColor(a)] - colorPriority[getDeviceAggregateColor(b)];
    if (colorDiff !== 0) return colorDiff;
    return a.name.localeCompare(b.name);
  };

  const siteGroups = new Map<string, { label: string; devices: DeviceResponseV5[] }>();
  for (const device of filteredDevices) {
    const site = getDeviceSite(device);
    const existing = siteGroups.get(site.code);
    if (existing) {
      existing.devices.push(device);
    } else {
      siteGroups.set(site.code, { label: site.label, devices: [device] });
    }
  }

  // Sort groups alphabetically, "Unknown" at the bottom
  const sortedGroups = [...siteGroups.entries()].sort(([codeA, groupA], [codeB, groupB]) => {
    if (codeA === 'Unknown') return 1;
    if (codeB === 'Unknown') return -1;
    return groupA.label.localeCompare(groupB.label);
  });

  // Sort devices within each group
  for (const [, group] of sortedGroups) {
    group.devices.sort(sortByColorThenName);
  }

  if (devices.length === 0) {
    return (
      <Box padding={4}>
        <Text color="slate.500">No devices found in this workspace.</Text>
      </Box>
    );
  }

  return (
    <Box padding={4}>
      <Flex alignItems="center" gap={3} mb={4}>
        <Text fontSize="lg" fontWeight="bold">
          Status
        </Text>
        <Text color="slate.400" fontSize="sm">
          Auto-refreshes every 30s
        </Text>
        <Tooltip label="Legend">
          <Button aria-label="Legend" ml="auto" onClick={() => setShowLegend((v) => !v)} size="sm">
            <Icon as={CircleQuestionSolid} />
          </Button>
        </Tooltip>
      </Flex>
      {showLegend && (
        <Box
          background="white"
          border="1px solid"
          borderColor="slate.200"
          borderRadius="md"
          mb={4}
          px={6}
          py={4}
        >
          <Text color="slate.700" fontWeight="bold" mb={3}>
            Traffic Light Legend
          </Text>
          <Flex flexWrap="wrap" gap={8}>
            <Flex flexDirection="column" gap={2}>
              <Text color="slate.700" fontSize="sm" fontWeight="semibold">
                Containers
              </Text>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="green" label="Containers: green" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  All running or exited cleanly (code 0)
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="yellow" label="Containers: yellow" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Some in transitional state
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="red" label="Containers: red" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  One or more crashed or dead
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="gray" label="Containers: gray" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  No status reported
                </Text>
              </Flex>
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text color="slate.700" fontSize="sm" fontWeight="semibold">
                Connectivity
              </Text>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="green" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Device connected
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="red" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Device disconnected
                </Text>
              </Flex>
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text color="slate.700" fontSize="sm" fontWeight="semibold">
                Deployment
              </Text>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="green" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Running expected fleet deployment
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="yellow" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Running a different deployment
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="red" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Deployment failed
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="gray" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  No deployment data
                </Text>
              </Flex>
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text color="slate.700" fontSize="sm" fontWeight="semibold">
                OS Version
              </Text>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="green" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Major version is supported
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="red" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  Major version no longer supported
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <TrafficLightDot color="gray" label="" size="10px" />
                <Text color="slate.600" fontSize="xs">
                  No OS data
                </Text>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <Box as="span" color="yellow.500" display="inline-flex" flexShrink={0}>
                  <Icon as={StatusTriangleExclamationSolid} boxSize="10px" />
                </Box>
                <Text color="slate.600" fontSize="xs">
                  OS update available
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      )}
      <Box maxWidth="300px" mb={4}>
        <Input
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Filter by name, workcenter or location..."
          size="sm"
          value={nameFilter}
        />
      </Box>
      <Flex flexDirection="column" gap={6}>
        {sortedGroups.map(([code, group]) => (
          <Box key={code}>
            <Text fontSize="md" fontWeight="semibold" mb={3}>
              {group.label}
            </Text>
            <Flex flexWrap="wrap" gap={4}>
              {group.devices.map((device) => (
                <DeviceStatusCard
                  canOperate={canOperate}
                  device={device}
                  fleetActiveDeploymentIds={fleetActiveDeploymentIds}
                  fleetNames={fleetNames}
                  key={device.id}
                />
              ))}
            </Flex>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};
